import { useState } from 'react';
import { useChat } from '../store';
import { Avatar } from './Avatar';
import { api, ApiError } from '../api';
import { IconPhone, IconVideo } from './Icon';
import type { ActiveCallInfo } from './CallModal';
import type { ConversationListEntry } from '../types';
import { isReplyWindowOpen, replyWindowClosedLabel } from '../bridge';
import { TemplatePickerModal } from './TemplatePickerModal';

interface ChatHeaderProps {
  conversation: ConversationListEntry;
  onStartCall: (info: ActiveCallInfo) => void;
  onOpenInfo: () => void;
}

export function ChatHeader({ conversation, onStartCall, onOpenInfo }: ChatHeaderProps) {
  const [callError, setCallError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  async function startCall(video: boolean) {
    setCallError(null);
    setStarting(true);
    try {
      const res = await api.startCall(conversation.id, video);
      onStartCall({
        roomName: res.roomName,
        conversationId: conversation.id,
        url: res.url,
        token: res.token,
        video,
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'calls_not_configured') {
        setCallError('Calls require LiveKit setup.');
      } else {
        setCallError('Could not start call.');
      }
      window.setTimeout(() => setCallError(null), 4000);
    } finally {
      setStarting(false);
    }
  }
  const me = useChat((s) => s.me);
  const onlineUserIds = useChat((s) => s.onlineUserIds);

  const others = conversation.participants.filter((p) => p.id !== me?.id);

  let title: string;
  let subtitle: string;
  let avatarKey: { id: string; displayName: string | null; email: string };
  let online: boolean | undefined;

  if (conversation.type === 'dm') {
    const other = others[0];
    if (!other) {
      title = 'Empty conversation';
      subtitle = '';
      avatarKey = { id: conversation.id, displayName: null, email: '?' };
    } else {
      title = other.displayName ?? other.email;
      online = onlineUserIds[other.id] ?? false;
      subtitle = online ? 'Online' : other.email;
      avatarKey = { id: other.id, displayName: other.displayName, email: other.email };
    }
  } else {
    title =
      conversation.name ??
      others.map((p) => p.displayName ?? p.email).join(', ');
    subtitle = `${conversation.participants.length} members`;
    avatarKey = { id: conversation.id, displayName: title, email: title };
  }

  if (conversation.bridge?.network === 'whatsapp') {
    subtitle = conversation.bridge.displayPhoneNumber
      ? `WhatsApp - ${conversation.bridge.displayPhoneNumber}`
      : 'WhatsApp';
  }

  return (
    <>
      <header className="sticky top-0 z-10 glass-chrome hairline px-4 py-3 flex items-center gap-3 shrink-0">
        <button
          onClick={onOpenInfo}
          className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer hover:bg-zinc-900/40 -mx-2 px-2 py-1 rounded-xl transition-all duration-300 ease-spring text-left"
          aria-label="Conversation info"
        >
          <Avatar
            id={avatarKey.id}
            displayName={avatarKey.displayName}
            email={avatarKey.email}
            online={online}
            size={36}
            kind={conversation.type === 'dm' ? 'user' : 'conversation'}
            avatarUpdatedAt={
              conversation.type === 'dm' ? undefined : conversation.avatarUpdatedAt
            }
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold tracking-tight truncate">{title}</h2>
            <p className="text-[11px] text-zinc-400 truncate">
              {callError ? <span className="text-rose-400">{callError}</span> : subtitle}
            </p>
          </div>
        </button>
        <CircleIconButton onClick={() => void startCall(false)} disabled={starting} title="Voice call">
          <IconPhone size={16} strokeWidth={1.5} />
        </CircleIconButton>
        <CircleIconButton onClick={() => void startCall(true)} disabled={starting} title="Video call">
          <IconVideo size={16} strokeWidth={1.5} />
        </CircleIconButton>
      </header>
      {conversation.bridge?.network === 'whatsapp' &&
        !isReplyWindowOpen(conversation.bridge.lastInboundAt) && (
          <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-[11px] text-amber-300 flex items-center justify-between gap-2">
            <span>{replyWindowClosedLabel()}</span>
            <button
              onClick={() => setTemplateOpen(true)}
              className="shrink-0 rounded-lg px-2 py-1 text-[11px] font-medium bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition"
            >
              Send a template
            </button>
          </div>
        )}
      {templateOpen && (
        <TemplatePickerModal
          conversationId={conversation.id}
          onClose={() => setTemplateOpen(false)}
        />
      )}
    </>
  );
}

function CircleIconButton({
  onClick,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="group w-9 h-9 rounded-xl bg-zinc-900/60 hover:bg-zinc-900 hairline text-zinc-400 hover:text-zinc-100 disabled:opacity-50 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center"
    >
      <span className="transition-transform duration-300 ease-spring group-hover:scale-110">
        {children}
      </span>
    </button>
  );
}
